import { useState } from 'react';
import {
  Button, Chip, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { approvalMatrixApi, type MatrixRule, type MatrixInput } from '../../api/engine.api';
import { rolesApi } from '../../api/roles.api';
import { extractApiError } from '../../api/error';

const MODULES = ['oms', 'boq', 'pr', 'po', 'wo', 'payment'];
const empty: MatrixInput = { moduleName: 'payment', amountFrom: 0, amountTo: null, approvalLevel: 1, approverRole: '', approvalMode: 'sequential', escalationDays: 1, status: 'active' };

export function ApprovalMatrixPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MatrixRule | null>(null);
  const [form, setForm] = useState<MatrixInput>(empty);
  const [deleting, setDeleting] = useState<MatrixRule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['approval-matrix'], queryFn: () => approvalMatrixApi.list({ limit: 100 }) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['approval-matrix'] });

  const save = useMutation({
    mutationFn: () => (editing ? approvalMatrixApi.update(editing.id, form) : approvalMatrixApi.create(form)),
    onSuccess: () => { setOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({ mutationFn: (id: number) => approvalMatrixApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const openNew = () => { setEditing(null); setForm(empty); setError(null); setOpen(true); };
  const openEdit = (r: MatrixRule) => { setEditing(r); setForm({ moduleName: r.moduleName, clientId: r.clientId, stateId: r.stateId, regionId: r.regionId, amountFrom: r.amountFrom, amountTo: r.amountTo, approvalLevel: r.approvalLevel, approverRole: r.approverRole, approvalMode: r.approvalMode, escalationDays: r.escalationDays, status: r.status }); setError(null); setOpen(true); };

  const columns: Column<MatrixRule>[] = [
    { key: 'moduleName', label: 'Module' },
    { key: 'amountFrom', label: 'Amount Band', render: (r) => `₹${r.amountFrom.toLocaleString()} – ${r.amountTo === null ? '∞' : '₹' + r.amountTo.toLocaleString()}` },
    { key: 'approvalLevel', label: 'Level', width: 70 },
    { key: 'approverRole', label: 'Approver Role' },
    { key: 'approvalMode', label: 'Mode', render: (r) => <Chip size="small" label={r.approvalMode} /> },
    { key: 'escalationDays', label: 'Escalate (days)', width: 120 },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  const set = <K extends keyof MatrixInput>(k: K, v: MatrixInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <PageHeader title="Approval Matrix" subtitle="Amount / client / region-based multi-level approval rules." action={<Button startIcon={<Add />} variant="contained" onClick={openNew}>New Rule</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={1} limit={data?.data.length || 25} total={data?.meta?.total ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No approval rules yet"
        actions={(r) => (
          <>
            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
          </>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Rule' : 'New Approval Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField select label="Module" value={form.moduleName} onChange={(e) => set('moduleName', e.target.value)} fullWidth>
                {MODULES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField select label="Approver Role" value={form.approverRole} onChange={(e) => set('approverRole', e.target.value)} fullWidth>
                {(roles.data ?? []).map((r) => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Amount From" type="number" value={form.amountFrom} onChange={(e) => set('amountFrom', Number(e.target.value))} fullWidth />
              <TextField label="Amount To (blank = ∞)" type="number" value={form.amountTo ?? ''} onChange={(e) => set('amountTo', e.target.value ? Number(e.target.value) : null)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Level" type="number" value={form.approvalLevel} onChange={(e) => set('approvalLevel', Number(e.target.value))} fullWidth />
              <TextField select label="Mode" value={form.approvalMode} onChange={(e) => set('approvalMode', e.target.value)} fullWidth>
                <MenuItem value="sequential">Sequential</MenuItem><MenuItem value="parallel">Parallel</MenuItem>
              </TextField>
              <TextField label="Escalation Days" type="number" value={form.escalationDays} onChange={(e) => set('escalationDays', Number(e.target.value))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Client ID (optional)" type="number" value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value ? Number(e.target.value) : null)} fullWidth />
              <TextField label="Region ID (optional)" type="number" value={form.regionId ?? ''} onChange={(e) => set('regionId', e.target.value ? Number(e.target.value) : null)} fullWidth />
              <TextField label="State ID (optional)" type="number" value={form.stateId ?? ''} onChange={(e) => set('stateId', e.target.value ? Number(e.target.value) : null)} fullWidth />
            </Stack>
            <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)} fullWidth>
              <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.moduleName || !form.approverRole} onClick={() => save.mutate()}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete rule" message={`Delete this ${deleting?.moduleName} rule?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
